<template>
  <ValidationObserver ref="fleetUploadProcessing" id="fleetUploadProcessing" name="fleetUploadProcessing">

    <div class="card card-custom">
      <div class="card-body container">

        <b-alert v-if="errorAlert" variant="warning" show>
          <div class="text-dark" v-html="errorAlert"></div>
        </b-alert>

        <div v-if="appData">
          <b-alert v-if="isReadOnly && appData.formStatus === 'Accepted'" variant="secondary" show>
            <div class="text-dark">This form is already accepted and cannot be modified.</div>
          </b-alert>

          <div class="d-flex justify-content-center">
            <h4 class="font-weight-bold">
              Fleet Declaration Effective Period :
              <b-badge variant="primary" class="p-2">
                {{ appData.effectiveStartDate | dateParse("YYYY-MM-DD") | dateFormat("DD/MM/YYYY") }}
                to
                {{ appData.effectiveEndDate | dateParse("YYYY-MM-DD") | dateFormat("DD/MM/YYYY") }}
              </b-badge>
            </h4>
          </div>

          <div class="d-flex justify-content-center mb-10">
            <a v-if="appData.processedFleetDecLink" :href="appData.processedFleetDecLink" target="_blank" class="btn btn-primary mr-5">View Final Accepted Fleet</a>

            <a v-if="appData.clientFleetDecLink" :href="appData.clientFleetDecLink" target="_blank" class="btn btn-outline-primary">View Client's Original Fleet Declaration</a>
          </div>

          <table class="table table-bordered" v-if="!isReadOnly">
            <tr>
              <td width="50%">
                <h4 class="font-weight-bold">
                  Form Fleet <span v-if="!fleetLookupRunning && !fleetExtractionRunning && appData.fleetInfo.fleetCount > 0"> Size: <strong>{{ appData.fleetInfo.fleetCount }}</strong></span>
                </h4>

                <div>
                  <button v-if="appData.fleetInfo.fleetCount > 0" class="btn btn-primary btn-sm mr-5" @click.prevent="downloadTransactionFleet"
                          :disabled="isReadOnly || appData.fleetInfo.downloadingTransactionFleet || fleetExtractionRunning || fleetLookupRunning">
                    <i class="fa fa-download fa-sm"></i> Download Fleet
                  </button>
                  <b-spinner class="ml-2 align-middle" v-if="appData.fleetInfo.downloadingTransactionFleet"></b-spinner>

                  <button class="btn btn-outline-primary btn-sm" @click.prevent="refreshFleetStatus(false)"
                          :disabled="appData.fleetInfo.downloadingTransactionFleet">
                    <i class="fa fa-redo"></i> Refresh Fleet
                  </button>
                </div>

                <h5 v-if="fleetLookupRunning" class="mt-5">
                  <b-spinner class="mr-2 align-middle"></b-spinner>
                  <span v-if="appData.fleetInfo.fleetCount && appData.fleetInfo.percentComplete">
                    Processing <span class="font-weight-boldest">{{appData.fleetInfo.fleetCount}}</span> vehicles... {{appData.fleetInfo.percentComplete}}% completed.
                    <b-progress class="mt-2" v-model="appData.fleetInfo.percentComplete" max="100" height="25px"></b-progress>
                  </span>
                  <span v-else>
                    Fleet processing in progress... please wait.
                </span>
                </h5>

                <div class="mt-5" v-if="!fleetLookupRunning && !fleetExtractionRunning">
                  <div v-if="appData.fleetInfo.fleetCount > 0">
                    <h6 v-if="appData.fleetInfo.fleetCount > threshold" class="text-dark">
                      Due to large fleet size, the vehicle details are not shown below. Please download the fleet details using the button below.
                    </h6>

                    <div v-if="appData.fleetInfo.incompleteCount > 0" class="mt-5 text-danger">
                      <strong>WARNING - {{ appData.fleetInfo.incompleteCount }} {{ appData.fleetInfo.incompleteCount === 1 ? 'vehicle has' : 'vehicles have' }} incomplete information - please review and update.</strong>
                    </div>
                  </div>
                </div>
              </td>
              <td width="50%" v-if="!isReadOnly && !appData.fleetInfo.downloadingTransactionFleet">
                <fleet-extractor @uploadFleetFiles="confirmNewFleetUpload" title="Upload Revised Fleet (Excel/CSV)"
                                  :extraction-job="appData.fleetInfo.fleetExtractionJobInfo"/>
              </td>
            </tr>
          </table>

          <div v-if="showFleetTable" class="mt-10">
            <ValidationObserver ref="vehiclesList">
              <vehicles-list :vehicles="appData.fleetInfo.fleet" :vehicle-types="appData.vehicleTypes"
                             :is-read-only="isReadOnly || !editingFleet" :mandatory="true"
              />
            </ValidationObserver>

            <div v-if="!isReadOnly" class="mt-5">
              <div v-if="fleetInvalid" class="text-danger">Please fix the validation errors in the fleet above.</div>
              <button class="btn btn-primary" v-if="!editingFleet" @click.prevent="editFleet">
                <i class="fa fa-edit"></i> Edit Fleet
              </button>
              <button class="btn btn-primary" v-if="editingFleet" @click.prevent="saveFleet">
                <i class="fa fa-save"></i> Save Fleet
              </button>
              <button class="btn btn-secondary ml-5" v-if="editingFleet" @click.prevent="cancelEditMode">
                <i class="fa fa-times"></i> Cancel
              </button>
            </div>
          </div>

          <b-modal ref="submitFleetModal" title="Submit Final Fleet" size="md" no-close-on-backdrop hide-header-close @ok="submitFinalFleet">
            <div class="form-group">
              Accepting this fleet declaration will update policy <strong>{{ appData.policyNumber }}</strong> with the new fleet details.<br/><br/>
              Are you sure you want to continue?
            </div>
          </b-modal>

          <div class="d-flex justify-content-center mt-5" v-if="!isReadOnly">
            <button class="btn btn-success font-weight-bold text-uppercase px-9 py-4"
                    :disabled="fleetInvalid || editingFleet || fleetExtractionRunning || fleetLookupRunning" @click.prevent="confirmFinalSubmission">
              Submit Final Fleet
            </button>
          </div>
        </div>
      </div>
    </div>
  </ValidationObserver>
</template>

<style lang="scss">

body.swal2-shown > [aria-hidden="true"] {
  transition: 0.1s filter;
  filter: blur(10px);
}

</style>

<script>
import _ from "lodash";
import FormData from "form-data";
import Swal from "sweetalert2";
import KTUtil from "@/assets/js/components/util";
import BrandService from "@/core/services/brand.service";
import VehiclesList from "@/view/pages/wizard/components/VehiclesList.vue";
import FleetExtractor from "@/view/pages/wizard/components/FleetExtractor.vue";
import {ADD_BODY_CLASSNAME, REMOVE_BODY_CLASSNAME} from "@/core/services/store/htmlclass.module";
import ApiService from "@/core/services/api.service";

export default {
  name: "FleetUploadProcessing",

  components: { FleetExtractor, VehiclesList },

  data() {
    return {
      formId: null,
      transactionId: null,
      appData: null,
      errorAlert: null,

      editingFleet: false,
      fleetCopy: null,
      fleetInvalid: false
    };
  },

  mounted() {
    this.startPageLoading();

    // Extract information from URL params
    const queryParams = this.$route.query;
    if (!queryParams.formId) {
      this.handleError("Unauthorised access!");
      return;
    }

    // Extract data from URL params
    this.formId = queryParams.formId;
    this.transactionId = queryParams.transactionId;

    // Get form data.
    this.getFormData();
  },

  computed: {
    isReadOnly: function () {
      if(this.appData) {
        return this.appData.formStatus !== "Active";
      }
      return true;
    },

    fleetLookupRunning: function () {
      return this.appData?.fleetInfo?.K_FleetLookupRunning || false;
    },

    fleetExtractionRunning: function () {
      return this.appData?.fleetInfo.K_FleetExtractionRunning || false;
    },

    threshold: function () {
      if(this.appData) {
        return this.appData?.fleetInfo?.fleetUiThreshold;
      }
      return null;
    },

    showFleetTable: function () {
      return this.appData
          && !this.isReadOnly
          && !this.fleetLookupRunning
          && !this.fleetExtractionRunning
          && this.appData.fleetInfo.fleetCount > 0
          && this.appData.fleetInfo.fleetCount <= this.threshold
          && Array.isArray(this.appData.fleetInfo.fleet);
    }
  },

  methods: {

    getFormData: function () {
      ApiService.get(`/fleet-upload/processing/form-data`,
      { params: { formId: this.formId, transactionId: this.transactionId } })
          .then(({ data }) => {
            this.appData = data.result;

            this.formId = this.appData.formId;
            this.transactionId = this.appData.transactionId;

            // Set UI Brand
            BrandService.setUIBrand(this.appData.brand);

            // Set wizard title and subtitle
            this.$store.commit("setWizardTitle", `Fleet Upload: ${this.appData.policyNumber}`);
            this.$store.commit("setWizardSubtitle", this.appData.clientName);

            // Validate fleet if available
            if(this.showFleetTable) {
              this.fleetInvalid = _.some(this.appData.fleetInfo.fleet, { 'dataMissing': true});
            }

            this.stopPageLoading();
            this.refreshFleetStatus(true);
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    confirmNewFleetUpload: function (selectedFiles) {
      this.clearError();

      if(this.fleetExtractionRunning || this.fleetLookupRunning) {
        // Ask for confirmation about starting a new job
        const message = `Previous fleet processing job is still in progress. <br/><br/>Are you sure you want to start over?`;
        Swal.fire({
          titleText: `Fleet Processing`,
          html: `<p>${message}</p>`,
          icon: "warning",
          allowOutsideClick: false,
          allowEnterKey: false,
          confirmButtonText: 'Yes',
          showConfirmButton: true,
          showCancelButton: true,
          showCloseButton: false
        })
            .then(result => {
              if(result && result.isConfirmed) {
                this.uploadFleetFiles(selectedFiles);
              }
            });
      }
      else {
        this.uploadFleetFiles(selectedFiles);
      }
    },

    uploadFleetFiles: function(selectedFiles) {
      this.startPageLoading();
      const formData = new FormData();
      for (const [i, file] of selectedFiles.entries()) {
        formData.append(`inputFiles[${i}]`, file);
      }

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' }
      };

      ApiService.post(`/fleet-upload/transaction/${this.transactionId}`, formData, config)
          .then(({ data }) => {
            const fleetInfo = data.result;
            this.$set(this.appData, 'fleetInfo', fleetInfo);
            this.stopPageLoading();
            this.refreshFleetStatus(true);
          })
          .catch((error) => {
            this.errorAlert = `Something went wrong! Please try again.`;
            console.log(error);
          });
    },

    refreshFleetStatus: function (silent) {
      this.clearError();

      if(!silent) {
        this.startPageLoading();
      }
      ApiService.post(`/nb/transaction-fleet/refresh`, {
        transactionId: this.transactionId,
      })
          .then(({ data }) => {
            const fleetInfo = data.result;
            this.$set(this.appData, 'fleetInfo', fleetInfo);

            if(this.fleetLookupRunning || this.fleetExtractionRunning) {
              setTimeout(() => {
                this.refreshFleetStatus(true)
              }, 3000);
            }
            this.stopPageLoading();
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    downloadTransactionFleet: function() {
      this.clearError();
      this.$set(this.appData.fleetInfo, 'downloadingTransactionFleet', true);

      ApiService.get(`/nb/transaction-fleet/download`,
          { params: { transactionId: this.transactionId }})
          .then(({ data }) => {
            const fleetCsvInfo = data.result;
            if(fleetCsvInfo) {
              this.$set(this.appData.fleetInfo, 'incompleteCount', fleetCsvInfo.incompleteCount);

              const url = window.URL.createObjectURL(new Blob([fleetCsvInfo.fleetCsv]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', `Form_Fleet.csv`);
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }
            else {
              this.setError(`Fleet data could not be downloaded. Please try again.`);
            }
            this.$set(this.appData.fleetInfo, 'downloadingTransactionFleet', false);
          })
          .catch((error) => {
            this.$set(this.appData.fleetInfo, 'downloadingTransactionFleet', false);
            this.handleError(error);
          });
    },

    saveTransactionFleet: function(newFleet) {
      this.clearError();
      this.startPageLoading();

      ApiService.post(`/nb/transaction-fleet`, {
        transactionId: this.transactionId,
        fleet: newFleet
      })
          .then(({ data }) => {
            const fleetInfo = data.result;
            this.$set(this.appData, 'fleetInfo', fleetInfo);

            this.stopPageLoading();
            this.refreshFleetStatus(true);
          })
          .catch((error) => {
            this.handleError(error);
          });
    },

    editFleet: function () {
      this.fleetInvalid = false;
      this.fleetCopy = _.cloneDeep(this.appData.fleetInfo.fleet);
      this.editingFleet = true;
    },

    saveFleet: async function () {
      const validFleet = await this.$refs.vehiclesList.validate();
      this.fleetInvalid = !validFleet;
      if(this.fleetInvalid) {
        return;
      }

      this.saveTransactionFleet(this.appData.fleetInfo.fleet);
      this.fleetCopy = null;
      this.editingFleet = false;
    },

    cancelEditMode: function () {
      this.$set(this.appData.fleetInfo, 'fleet', this.fleetCopy);
      this.fleetCopy = null;
      this.fleetInvalid = _.some(this.appData.fleetInfo.fleet, { 'dataMissing': true });
      this.editingFleet = false;
    },

    confirmFinalSubmission: function () {
      // TODO Check final fleet validity
      this.$refs["submitFleetModal"].show();
    },

    submitFinalFleet: function () {
      this.startPageLoading();
      ApiService.post(`/fleet-upload/processing/fleet-submit`,
          { transactionId: this.transactionId, formId: this.formId })
          .then(() => {
            this.$refs["submitFleetModal"].hide();
            this.stopPageLoading();
            Swal.fire({
              titleText: `Fleet Upload Initiated - Policy ${this.appData.policyNumber}`,
              html: `<p>Please allow a few minutes for the fleet to be fully updated in the CRM.</p>
                   <br/><span class="font-weight-bold">Please close this browser window.</span>`,
              icon: "success",
              width: "40rem",
              allowOutsideClick: false,
              allowEnterKey: false,
              allowEscapeKey: false,
              showConfirmButton: false,
              showCancelButton: false,
              showCloseButton: false
            });
          })
          .catch((error) => {
            console.log(error);
            this.handleError(error);
          });
    },

    setError: function (message) {
      KTUtil.scrollTop();
      this.errorAlert = message;
    },

    clearError: function () {
      this.errorAlert = null;
    },

    handleError: function(errorObj) {
      this.stopPageLoading();
      let errorMsg = null;

      if(errorObj) {
        let responseStatus = _.get(errorObj, "response.status");
        let errorMessage = errorObj.message || '';
        if (responseStatus === 401 || errorMessage.includes("status code 401") ||
            responseStatus === 403 || errorMessage.includes("status code 403")
        ) {
          errorMsg = "This session is invalid or expired. Please close this window.";
        }
        else {
          let responseResult = _.get(errorObj, "response.data.result");
          if (_.isString(responseResult)) {
            let applicationErrorPrefix = "UI_ERROR:";

            if (responseResult.startsWith(applicationErrorPrefix)) {
              errorMsg = responseResult.substring(applicationErrorPrefix.length, responseResult.length);
            }
          }
        }
      }
      if (!errorMsg) {
        errorMsg = "An unexpected error has occurred.";
      }

      this.setError(errorMsg);
      KTUtil.scrollTop();
    },

    startPageLoading: function() {
      this.$store.dispatch(ADD_BODY_CLASSNAME, "page-loading");
    },

    stopPageLoading: function() {
      this.$store.dispatch(REMOVE_BODY_CLASSNAME, "page-loading");
    }
  },
};
</script>
